import type { CartesianPose } from '@sb/geometry';
import { quaternionMultiply } from '@sb/geometry';
import * as log from '@sb/log';
import {
  BuiltInFrame,
  PrimitiveEventType,
  type Primitive,
  type PrimitiveEventMsg,
  type SpeedL,
} from '@sb/messenger/auto_generated/messenger/trajectory_controller_service';
import type {
  ArmControlBotConnector,
  TrajectoryControllerConnector,
} from '@sb/messenger/connectors/generated';
import type { JointNumber } from '@sb/motion-planning';
import { getFeatureFlagSync } from '@sb/service-interfaces/host/featureFlags';
import { StatefulEventEmitter, six } from '@sb/utilities';

import { enqueueWithTcPrerequisites } from './enqueueWithTcPrerequisites';
import { FailureKind } from './FailureKind';
import type {
  MotionCommands,
  MotionEvents,
  RoutineContext,
} from './RoutineContext';
import type { SpeedProfile } from './speed-profile';
import type { SpeedLJog, VelocityExpressedIn } from './types';
import { toMessengerQuaternion } from './util/messengerQuaternion';

const ns = log.namespace('JogMotionWithTC');

// RPC-level timeouts — how long to wait for a gRPC response, not how long the
// operation itself takes. Kept short so callers surface network issues quickly.
const ENQUEUE_TIMEOUT_MS = 3000;
const CANCEL_RPC_TIMEOUT_MS = 1000;
const SET_SAFETY_SESSION_TIMEOUT_MS = 3000;

// How long to wait for the STOPPED event after Stop completes before
// force-resolving. This starts *after* the RPC returns, so the full window
// is available for the event to arrive.
const CANCEL_EVENT_TIMEOUT_MS = 2000;

/**
 * Build a length-6 JointDirection (one-hot ±1) for a SpeedJ primitive.
 *
 * v0.4 TC composes speed at enqueue time from JogConfig × slider × active
 * SafetyLimits — the caller's SpeedProfile is no longer carried in the
 * wire message. `speedProfile.maxJointSpeeds[jointNumber]` is still inspected
 * for its SIGN, which determines jog direction. Magnitude is discarded.
 *
 * The TC accepts only one-hot ±1 directions. A zero (or non-finite)
 * requested velocity has no sign to extract, and defaulting it to a
 * direction would command motion the caller never asked for — throw
 * before anything is enqueued instead.
 */
export function computeJogDirection(
  jointNumber: JointNumber,
  speedProfile: SpeedProfile,
): number[] {
  const entries = six(0);
  const signed = speedProfile.maxJointSpeeds[jointNumber];

  if (!Number.isFinite(signed)) {
    throw new Error(
      `Cannot jog joint ${jointNumber}: requested velocity is ${signed} (not finite)`,
    );
  } else if (signed > 0) {
    entries[jointNumber] = 1;
  } else if (signed < 0) {
    entries[jointNumber] = -1;
  } else {
    throw new Error(
      `Cannot jog joint ${jointNumber}: requested velocity is ${signed} (no direction)`,
    );
  }

  return [...entries];
}

/**
 * Validate the SpeedL `direction` field.
 *
 * A jog command carries a direction, not a speed. Each entry must be
 * -1, 0, or +1. Exactly one entry must be nonzero. Any other value is
 * a caller bug. This function throws and does not coerce the value.
 * The bug then surfaces here, not as a TC INVALID_ARGUMENT rejection
 * after the enqueue round-trip.
 */
export function validateSpeedLDirection(
  direction: SpeedLJog['direction'],
): void {
  const entries = [...direction.linear, ...direction.angular];

  const axisNames = [
    'linear x',
    'linear y',
    'linear z',
    'angular x',
    'angular y',
    'angular z',
  ];

  const invalid = axisNames
    .map((name, i) => ({ name, value: entries[i] }))
    .filter(({ value }) => value !== -1 && value !== 0 && value !== 1);

  if (invalid.length > 0) {
    throw new Error(
      `Cannot jog tooltip: each direction entry must be -1, 0, or 1, got ${invalid
        .map(({ name, value }) => `${name} = ${value}`)
        .join(', ')}`,
    );
  }

  const nonzeroAxes = axisNames.filter((_, i) => entries[i] !== 0);

  if (nonzeroAxes.length !== 1) {
    throw new Error(
      `Cannot jog tooltip: direction must have exactly one nonzero entry, got ${
        nonzeroAxes.length === 0 ? 'none' : `[${nonzeroAxes.join(', ')}]`
      }`,
    );
  }
}

const IDENTITY_WIRE_ROTATION = { x: 0, y: 0, z: 0, w: 1 };

const IDENTITY_WIRE_POSE = {
  position: { x: 0, y: 0, z: 0 },
  orientation: IDENTITY_WIRE_ROTATION,
};

// Wire frames for each `velocityExpressedIn.parent` value. `reference`
// anchors the motion. `velocityExpressedIn` parents the jog axes. The
// record is exhaustive — the compiler rejects a new parent value until it
// gets a row here.
const BUILT_IN_FRAMES: Record<
  VelocityExpressedIn['parent'],
  { reference: BuiltInFrame; velocityExpressedIn: BuiltInFrame }
> = {
  world: {
    reference: BuiltInFrame.BUILT_IN_FRAME_WORLD,
    velocityExpressedIn: BuiltInFrame.BUILT_IN_FRAME_WORLD,
  },
  base: {
    reference: BuiltInFrame.BUILT_IN_FRAME_BASE,
    velocityExpressedIn: BuiltInFrame.BUILT_IN_FRAME_BASE,
  },
  tcp: {
    reference: BuiltInFrame.BUILT_IN_FRAME_WORLD,
    velocityExpressedIn: BuiltInFrame.BUILT_IN_FRAME_FLANGE,
  },
};

/**
 * Compose the SpeedL frame fields botman owns:
 *
 * - `reference`: the frame the motion is anchored to — BASE for a base
 *   jog, WORLD for the others. The TC validates it but does not resolve
 *   it. The two choices diverge only while the base moves.
 * - `commanded_frame`: the controlled point, as the full flange-to-TCP
 *   transform. Its position sets the pivot for rotational jogs and the
 *   point the TCP-linear speed limit binds to.
 * - `velocity_expressed_in`: the jog axes on the wire. For parent 'tcp',
 *   the wire parent is FLANGE and the flange-to-TCP rotation is composed
 *   in front of `rotationInParent`, so the axes are the displayed tooltip
 *   axes and not the bare flange axes.
 */
export function composeSpeedLFrames(
  velocityExpressedIn: VelocityExpressedIn,
  flangeToTcp: CartesianPose,
): Pick<SpeedL, 'reference' | 'commandedFrame' | 'velocityExpressedIn'> {
  const { parent, rotationInParent } = velocityExpressedIn;
  const wireFrames = BUILT_IN_FRAMES[parent];

  // The wire has no TCP frame: lower 'tcp' to FLANGE plus the
  // flange-to-TCP rotation.
  const wireRotation =
    parent === 'tcp'
      ? quaternionMultiply(flangeToTcp, rotationInParent)
      : rotationInParent;

  return {
    reference: {
      parent: wireFrames.reference,
      poseInParent: IDENTITY_WIRE_POSE,
    },
    commandedFrame: {
      parent: BuiltInFrame.BUILT_IN_FRAME_FLANGE,
      poseInParent: {
        position: { x: flangeToTcp.x, y: flangeToTcp.y, z: flangeToTcp.z },
        orientation: toMessengerQuaternion(flangeToTcp),
      },
    },
    velocityExpressedIn: {
      parent: wireFrames.velocityExpressedIn,
      rotationInParent: toMessengerQuaternion(wireRotation),
    },
  };
}

export class JogMotionWithTC {
  private routineContext: RoutineContext;

  private connector: TrajectoryControllerConnector;

  // TODO(tc-migration): remove once all motion goes through TC
  private armControlBotConnector: ArmControlBotConnector | undefined;

  // TODO(tc-migration): remove once all motion goes through TC
  private controlSourceAcquired: boolean = false;

  // TODO(tc-migration): remove once all motion goes through TC
  // Separate from controlSourceAcquired so cleanup() stays idempotent when
  // start()'s cancel-during-acquire branch also triggers a release.
  private controlSourceReleased: boolean = false;

  private events: StatefulEventEmitter<MotionEvents & MotionCommands>;

  private primitiveId: number | undefined;

  private cancelled: boolean = false;

  private resolved: boolean = false;

  private subscribed: boolean = false;

  private cancelTimeoutHandle: ReturnType<typeof setTimeout> | undefined;

  private pause3PEMonitor:
    | ReturnType<RoutineContext['createPause3PEMonitor']>
    | undefined;

  constructor(routineContext: RoutineContext) {
    if (!routineContext.trajectoryControllerConnector) {
      throw new Error('Could not find trajectoryControllerConnector');
    }

    this.routineContext = routineContext;
    this.connector = routineContext.trajectoryControllerConnector;
    // TODO(tc-migration): remove once all motion goes through TC
    this.armControlBotConnector = routineContext.armControlBotConnector;
    this.events = new StatefulEventEmitter<MotionEvents & MotionCommands>();

    this.setupEventListeners();

    // TODO: 3PE monitoring should be lifted to the caller so multiple
    // motion types share the same start/stop lifecycle instead of each
    // class wiring its own monitor.
    this.pause3PEMonitor = routineContext.createPause3PEMonitor(() => {
      this.events.emit('cancel');
    });
  }

  public getEvents(): StatefulEventEmitter<MotionEvents & MotionCommands> {
    return this.events;
  }

  public async start(
    primitive: Primitive,
    options: { manualRecoveryMode?: boolean } = {},
  ): Promise<void> {
    log.info(ns`start`, 'Starting JogMotionWithTC', { primitive });

    this.pause3PEMonitor?.start('jog motion (TC)');

    try {
      // ACB rejects SetControlSource while isInFailureState is set. During
      // manual recovery the routine-runner is still in Failure, so mirror
      // JogMotionWithROS and clear the flag before requesting control.
      if (options.manualRecoveryMode) {
        await this.routineContext.robot.setInFailureState(false, false);
      }

      // TODO(tc-migration): remove control source handshake once all motion goes through TC
      if (getFeatureFlagSync('useControlSourceManager', false)) {
        if (!this.armControlBotConnector) {
          throw new Error(
            'ArmControlBotConnector is not set when ControlSourceManager is enabled',
          );
        }

        log.info(ns`start`, 'Setting control source to TRAJECTORY_CONTROLLER');

        const result = await this.armControlBotConnector.SetControlSource({
          value: 'TRAJECTORY_CONTROLLER',
        });

        if (result.error) {
          throw new Error(`SetControlSource failed: ${result.error.message}`);
        }

        this.controlSourceAcquired = true;

        // Close the cancel-during-SetControlSource window. If cancel() fired
        // while we were awaiting acquisition, skip Enqueue (avoid an unintended
        // jog) and ensure the release runs — cleanup() may already have run
        // with controlSourceAcquired=false and skipped the release; the
        // controlSourceReleased guard keeps cleanup idempotent.
        if (this.cancelled) {
          log.warn(
            ns`start`,
            'Cancelled during SetControlSource; skipping Enqueue',
          );

          if (this.resolved) {
            // cleanup() already ran and skipped release — run it again to release.
            this.cleanup();
          } else {
            // resolve() triggers cleanup which releases (acquired=true now).
            this.resolve('cancelled');
          }

          return;
        }
      }

      // Recovers missing TC prerequisites (safety session, world_to_base —
      // e.g. after a TC restart) on TC_ERROR_NOT_READY. Both read lazily from
      // the routine context; recovery aborts if cancelled.
      const reply = await enqueueWithTcPrerequisites(
        this.connector,
        primitive,
        this.routineContext,
        {
          enqueueTimeoutMs: ENQUEUE_TIMEOUT_MS,
          setSafetySessionTimeoutMs: SET_SAFETY_SESSION_TIMEOUT_MS,
          isCancelled: () => this.cancelled,
        },
      );

      // If cancel() fired while we awaited Enqueue, the primitive may
      // already be orphaned — don't subscribe or set primitiveId.
      if (this.cancelled) {
        log.warn(ns`start`, 'Cancelled during Enqueue, issuing Stop');

        try {
          await this.connector.Stop({}, CANCEL_RPC_TIMEOUT_MS);
        } catch {
          // best-effort
        }

        this.resolve('cancelled');

        return;
      }

      if (!reply.accepted) {
        log.warn(ns`start`, 'Enqueue rejected', { error: reply.error });

        this.resolve('failure', {
          failure: { kind: FailureKind.ExecutionFailure },
          failureReason: `TC rejected primitive: error=${reply.error}`,
        });

        return;
      }

      this.primitiveId = reply.primitiveId;

      // Subscribe AFTER Enqueue — the RPC triggers service discovery so
      // the topic port is cached and the SUB socket connects immediately.
      // Only STOPPED/COMPLETE/FAILED matter (jog-end events) so there is
      // no race; they fire long after subscribe returns.
      this.connector.subscribeToPrimitiveEvent(
        this.onPrimitiveEvent.bind(this),
      );

      this.subscribed = true;

      log.info(ns`start`, 'Primitive enqueued', {
        primitiveId: this.primitiveId,
      });
    } catch (err: any) {
      log.error(ns`start`, 'Enqueue failed', err);

      this.resolve('failure', {
        failure: { kind: FailureKind.ExecutionFailure },
        failureReason: err.message ?? 'Enqueue RPC failed',
      });
    }
  }

  private onPrimitiveEvent(event: PrimitiveEventMsg): void {
    // Filter by primitive_id — the topic is a global bus
    if (
      event.primitiveId !== undefined &&
      event.primitiveId !== this.primitiveId
    ) {
      return;
    }

    switch (event.type) {
      case PrimitiveEventType.PRIMITIVE_COMPLETE:
        log.info(ns`onPrimitiveEvent`, 'Primitive complete', {
          primitiveId: event.primitiveId,
        });

        this.resolve('complete');
        break;

      case PrimitiveEventType.PRIMITIVE_STOPPED:
        log.info(ns`onPrimitiveEvent`, 'Primitive stopped', {
          primitiveId: event.primitiveId,
        });

        this.resolve('cancelled');
        break;

      case PrimitiveEventType.PRIMITIVE_FAILED:
        log.error(ns`onPrimitiveEvent`, 'Primitive failed', {
          primitiveId: event.primitiveId,
          error: event.error,
        });

        this.resolve('failure', {
          failure: { kind: FailureKind.ExecutionFailure },
          failureReason: `TC primitive failed: error=${event.error}`,
        });

        break;

      default:
        break;
    }
  }

  private resolve(
    eventName: 'complete' | 'cancelled' | 'failure',
    payload?: any,
  ): void {
    if (this.resolved) {
      return;
    }

    this.resolved = true;
    this.cleanup();

    if (payload) {
      this.events.emit(eventName, payload);
    } else {
      this.events.emit(eventName);
    }
  }

  private cleanup(): void {
    this.pause3PEMonitor?.stop();

    // TODO(tc-migration): remove control source handshake once all motion goes through TC
    if (
      this.controlSourceAcquired &&
      !this.controlSourceReleased &&
      this.armControlBotConnector
    ) {
      this.controlSourceReleased = true;

      log.info(ns`cleanup`, 'Releasing control source TRAJECTORY_CONTROLLER');

      this.armControlBotConnector
        .ReleaseControlFromSource({
          value: 'TRAJECTORY_CONTROLLER',
        })
        .catch((err) => {
          log.warn(
            ns`cleanup`,
            'ReleaseControlFromSource failed during cleanup',
            err,
          );
        });
    }

    if (this.cancelTimeoutHandle) {
      clearTimeout(this.cancelTimeoutHandle);
      this.cancelTimeoutHandle = undefined;
    }

    if (this.subscribed) {
      try {
        this.connector.unsubscribeFromPrimitiveEvent();
      } catch (err) {
        log.warn(ns`cleanup`, 'Failed to unsubscribe from PrimitiveEvent', err);
      }

      this.subscribed = false;
    }
  }

  private async cancel(): Promise<void> {
    if (this.cancelled) {
      return;
    }

    this.cancelled = true;
    log.info(ns`cancel`, 'Cancelling JogMotionWithTC');

    try {
      await this.connector.Stop({}, CANCEL_RPC_TIMEOUT_MS);
    } catch (err) {
      log.warn(ns`cancel`, 'Stop RPC failed', err);
    }

    // Start the fallback timeout *after* FastStop returns so the full
    // window is available for the STOPPED event to arrive.
    if (!this.resolved) {
      this.cancelTimeoutHandle = setTimeout(() => {
        log.warn(
          ns`cancel`,
          'No STOPPED event received within timeout, forcing cancel',
        );

        this.resolve('cancelled');
      }, CANCEL_EVENT_TIMEOUT_MS);
    }
  }

  private setupEventListeners(): void {
    this.events.on('cancel', async () => {
      this.pause3PEMonitor?.stop();
      await this.cancel();
    });

    this.events.on('failure', () => {
      log.info(ns`failure`, 'Motion failure');
      this.cancelled = true;
      this.pause3PEMonitor?.stop();
    });
  }
}
